import type { VocabularyOverrides } from "@norkron/web-privileges-admin";
import { CAPABILITIES } from "./roles";

/**
 * Presentation overrides for the privz access console.
 *
 * ## What this is, and what it is emphatically not
 *
 * `@norkron/web-privileges-admin` is product-AGNOSTIC by construction: the
 * capability VOCABULARY is server data it fetches, never a list a host ships. So
 * this file supplies no capabilities. It supplies COPY — the label and the
 * one-line hint elaborate already writes beside each capability in its own
 * permission matrix — keyed by capability string, so a capability the server
 * returns and elaborate has a better sentence for gets that sentence, and one it
 * does not know about renders from the server's own text.
 *
 * The copy is READ FROM `lib/roles.ts`, not re-typed here. That file is the
 * existing source of truth for the permission-matrix vocabulary and mirrors the
 * server's `auth.capabilities`; a second hand-maintained copy of eleven labels is
 * how the two come to disagree.
 *
 * ⛔ `lib/roles.ts` is NOT modified and `PermissionMatrix.tsx` is NOT replaced.
 * `DEFAULT_MATRIX` / `defaultAllowed` back `canInWorkspace`, which gates
 * create-article and about half the app across five screens. RULE 11.5: the new
 * surface lands first and proves itself; retiring the old one is a later, evidenced
 * step, not a side effect of this one.
 *
 * ## The genericity guard
 *
 * ⛔ `group` is STRUCTURAL — it becomes a DOM id and a testid — so
 * `assertGenericVocabulary` screens it for product tokens. `elaborate` is on the
 * lib's AMBIGUOUS list and `cerebr8` on its STRICT one, so neither may appear in a
 * group id, a capability key or a bundle id. `label` / `description` are display
 * copy and are deliberately left alone by the guard.
 *
 * The two group ids below are chosen for that reason: `document` and `workspace`
 * are what these capabilities are ABOUT, and are words no product owns.
 */

/**
 * Which cluster each of elaborate's eleven workspace capabilities belongs to.
 *
 * ⚠️ Only the keys that need a group are listed. The lib's `deriveGroup` reads the
 * SHAPE of a dotted key (`a.b.c` → `b`), which is the right default for a
 * namespaced vocabulary; elaborate's keys are flat single words (`view_doc`,
 * `comment`), where shape carries no information and every capability would become
 * its own group of one.
 */
const CAPABILITY_GROUP: Readonly<Record<string, string>> = {
  view_doc: "document",
  comment: "document",
  resolve_comment: "document",
  edit_doc: "document",
  propose_edit: "document",
  accept_proposal: "document",
  export_deliver: "document",
  restore_version: "document",
  manage_members: "workspace",
  manage_permissions: "workspace",
  admin_workspace: "workspace",
};

/**
 * Capabilities that rewrite delivered client work or hand out authority. Rendered
 * with the lib's elevated-risk treatment.
 *
 * `restore_version` is here because its own hint says what it does: it puts a past
 * version back as the head, which rewrites a DELIVERED document, moves the
 * delivery-gate hash and stales every pending proposal.
 */
const SENSITIVE = new Set([
  "restore_version",
  "export_deliver",
  "manage_members",
  "manage_permissions",
  "admin_workspace",
]);

const GROUP_LABELS: Readonly<Record<string, string>> = {
  document: "Documents & review",
  workspace: "Workspace administration",
};

/**
 * The overrides handed to `PrivilegesAdminProvider`.
 *
 * Built by mapping over `CAPABILITIES` rather than by listing keys, so a
 * capability added to `lib/roles.ts` arrives here with its label and hint already
 * attached — and one whose `group` is missing from the table above surfaces as a
 * failing test rather than as a silent singleton group.
 */
export const PRIVZ_VOCABULARY: VocabularyOverrides = {
  capabilities: Object.fromEntries(
    CAPABILITIES.map((c, index) => [
      c.key,
      {
        label: c.label,
        description: c.hint,
        group: CAPABILITY_GROUP[c.key] ?? "workspace",
        groupLabel: GROUP_LABELS[CAPABILITY_GROUP[c.key] ?? "workspace"],
        // Display order follows elaborate's own matrix column order, which runs
        // read → write → administer. The lib falls back to the capability string
        // for ties, so this only has to be stable, not dense.
        order: index,
        sensitive: SENSITIVE.has(c.key),
      },
    ]),
  ),
  groups: GROUP_LABELS,
  groupOrder: ["document", "workspace"],
};

/** The group ids this override set introduces — asserted generic by its test. */
export const PRIVZ_VOCABULARY_GROUPS = Object.keys(GROUP_LABELS);
